// src/lib/billing/comp-expiry-notice.ts
//
// What (if anything) to tell someone on an admin-granted complimentary plan
// whose grant is about to run out.
//
// WHY THIS EXISTS. On 2026-08-26 there were twenty active comps in production,
// fifteen of them expiring on the SAME DAY five days later, none with a card on
// file. What those fifteen would have experienced is Pro features turning into
// upgrade prompts overnight with no explanation anywhere in the product: the
// only place the app ever mentions a comp is one line of muted text inside the
// billing card on /settings, which does not name the date even though it is
// loaded three lines above the copy.
//
// The post-expiry email already existed (cron/comp-expiry, daily at 15:00) and
// it is a POST-MORTEM — it tells someone their access has already ended. This
// module powers the warning that comes first, on the banner and in the mail,
// from the same countdown so the two can never disagree.
//
// Pure and DB-free, like its sibling past-due-banner.ts, so the boundaries can
// be tested without a database.
//
// THE DATE RULE (same as school access, src/lib/school/license-dates.ts). A
// grant's compExpiresAt is the EXCLUSIVE instant Pro stops — 00:00 in the
// recipient's zone on the day AFTER the last day the admin picked — and every
// entitlement check is `compExpiresAt > now`. What a person reads is the LAST
// COVERED DAY (the day holding end - 1 ms) in their zone, worded "through".
// Printing the instant itself names the day after the last day, which is how a
// grant picked as "Oct 14" used to be announced as ending "Oct 13".

import { SCHOOL_TIME_ZONE } from '@/lib/school/license-dates'
import {
  lastCoveredDateString,
  lastCoveredDayLabel,
  safeZone,
  userDateString,
} from '@/lib/time/user-day'

export const COMP_EXPIRY_NOTICE_DAYS = 7

/**
 * The zone a comp's dates are read in: the recipient's stored zone, or Eastern
 * when they have none yet.
 *
 * Eastern, not UTC: User.timezone is empty only until the browser first
 * reports one, the product is US-facing, and the school pilot is in New
 * Jersey — the same fallback the school surfaces use (SCHOOL_TIME_ZONE). Read
 * in UTC, a last day stored as Eastern midnight is announced as the NEXT day,
 * the off-by-one this rule exists to remove. The grant routes convert the
 * picked day with this same function, so the date shown is the date picked.
 *
 * /api/me/timezone refuses unknown zones, so the invalid branch is a guard, not
 * a path real rows take.
 */
export function compTimeZone(timezone: string | null | undefined): string {
  if (!timezone) return SCHOOL_TIME_ZONE
  const zone = safeZone(timezone)
  return zone === 'UTC' && timezone !== 'UTC' ? SCHOOL_TIME_ZONE : zone
}

/**
 * "Oct 14, 2026" — the last day of a comp, in the recipient's zone. Always
 * worded "through {label}", never "until": "until Oct 14" reads as stopping
 * when Oct 14 begins, a day short of a grant that covers all of it.
 */
export function compLastDayLabel(
  timezone: string | null | undefined,
  compExpiresAt: Date | string,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
): string {
  return lastCoveredDayLabel(compTimeZone(timezone), compExpiresAt, options)
}

export type CompExpiryNoticeState = {
  /**
   * Calendar days, in the recipient's zone, from today to the LAST covered
   * day — 0 means today is the last day, 1 means tomorrow is.
   */
  daysLeft: number
  /** When the grant actually lapses (exclusive — Pro stops AT this instant). */
  endsAt: Date
  /** "Pro" / "Premium" — what they lose. */
  planName: string
  /** Today or tomorrow — warrants firmer wording. */
  urgent: boolean
}

export type CompExpiryInput = {
  compAccess: string | null
  compExpiresAt?: Date | null
  /** A live Stripe subscription means the comp lapsing takes nothing away. */
  subscriptionStatus?: string | null
  /** The recipient's stored zone — whose calendar the countdown counts in. */
  timezone?: string | null
}

const DAY_MS = 24 * 60 * 60 * 1000

/** Whole days from one YYYY-MM-DD to another (calendar dates, no zone left). */
function daysBetween(fromYmd: string, toYmd: string): number {
  const [fy, fm, fd] = fromYmd.split('-').map(Number)
  const [ty, tm, td] = toYmd.split('-').map(Number)
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / DAY_MS)
}

/** Comps are stored as the broader SubscriptionPlan, not the narrower PlanKey. */
export function compPlanName(compAccess: string | null): string {
  return compAccess === 'PREMIUM' ? 'Premium' : 'Pro'
}

/**
 * The notice to show, or null — which is the answer for almost everyone.
 *
 * Four separate reasons to stay silent, and each is a case where showing the
 * notice would say something untrue:
 *
 *   - NO EXPIRY SET. An open-ended grant has no deadline to warn about, and
 *     three of the twenty live grants are open-ended. Inventing one would be
 *     worse than saying nothing.
 *   - ALREADY LAPSED. The countdown would run negative and the sentence would
 *     be false — they have not "got 5 days left", they have nothing left. The
 *     post-expiry email owns that moment.
 *   - MORE THAN A WEEK OUT. A banner that lives on the page for a month is
 *     furniture by the time it matters.
 *   - PAYING ANYWAY. Comp beats Stripe inside getEffectivePlan, so someone can
 *     hold both. When the grant lapses that person keeps Pro through their
 *     subscription and loses nothing, so warning them about losing access is
 *     simply wrong. Zero of the twenty are in this state today, and the day one
 *     is, the check is what keeps us honest.
 */
export function compExpiryNoticeState(
  user: CompExpiryInput,
  now: Date = new Date(),
): CompExpiryNoticeState | null {
  if (!user.compAccess) return null
  if (user.compExpiresAt == null) return null
  if (user.subscriptionStatus === 'active') return null

  const endsAtMs = user.compExpiresAt.getTime()
  const nowMs = now.getTime()
  // Lapsed is the entitlement's own test (getEffectivePlan: > now), so the
  // notice can never outlive the access it describes.
  if (endsAtMs <= nowMs) return null

  // CALENDAR DAYS, not 24-hour blocks. Hours from now to the exclusive end
  // only match the calendar when the end is midnight where they live: a grant
  // that stops at 11 am tomorrow is 13 hours away at 10 pm, which hour-counting
  // called "ends today", and a 25-hour DST day added a day. Counting from
  // today to the last covered day, both in their zone, makes "ends today" mean
  // exactly "today is your last day" — the day the email prints beside it.
  // endsAt > now guarantees the last day is today or later.
  const zone = compTimeZone(user.timezone)
  const daysLeft = daysBetween(
    userDateString(zone, now),
    lastCoveredDateString(zone, user.compExpiresAt),
  )
  if (daysLeft >= COMP_EXPIRY_NOTICE_DAYS) return null

  return {
    daysLeft,
    endsAt: new Date(endsAtMs),
    planName: compPlanName(user.compAccess),
    urgent: daysLeft <= 1,
  }
}

/**
 * Notice copy.
 *
 * Says COMPLIMENTARY every time. These are people who were given something,
 * and a bare "your Pro access ends Friday" reads like a billing failure they
 * need to go and fix. It also never says "upgrade": nothing is being upgraded,
 * a gift is running out, and the difference is the whole tone of the message.
 */
export function compExpiryNoticeMessage(state: CompExpiryNoticeState): string {
  const what = `Your complimentary ${state.planName} access`
  if (state.daysLeft <= 0) return `${what} ends today.`
  if (state.daysLeft === 1) return `${what} ends tomorrow.`
  return `${what} ends in ${state.daysLeft} days.`
}
